// app/javascript/controllers/job_carousel_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slider", "prevBtn", "nextBtn", "dot", "dots"]

  connect() {
    this.currentSlide = 0
    this.setupResponsiveItems()
    this.updateView()
    this.setupTouchEvents()
    
    // Store bound handlers as instance properties
    this.boundHandleResize = this.handleResize.bind(this)
    
    // Update on window resize
    window.addEventListener('resize', this.boundHandleResize)
  }

  disconnect() {
    // Remove event listeners using the stored bound references
    window.removeEventListener('resize', this.boundHandleResize)
    this.removeTouchEvents()
  }

  setupResponsiveItems() {
    const slider = this.sliderTarget
    this.mobileItems = parseInt(slider.dataset.mobileItems, 10) || 1
    this.tabletItems = parseInt(slider.dataset.tabletItems, 10) || 2
    this.desktopItems = parseInt(slider.dataset.desktopItems, 10) || 4
    
    // Parse and validate totalItems with safe range clamping
    const parsedTotalItems = parseInt(slider.dataset.totalItems, 10) || 0
    const maxAllowedItems = slider.children.length
    this.totalItems = Math.max(0, Math.min(parsedTotalItems, maxAllowedItems))
  }

  getCurrentItemsPerView() {
    const width = window.innerWidth
    if (width < 640) { // sm breakpoint
      return this.mobileItems
    } else if (width < 1024) { // lg breakpoint
      return this.tabletItems
    } else {
      return this.desktopItems
    }
  }

  getMaxSlides() {
    const itemsPerView = Math.max(1, this.getCurrentItemsPerView()) // Guard against zero/divide
    const pages = Math.ceil(this.totalItems / itemsPerView) - 1
    return Math.max(0, pages)
  }

  prev() {
    if (this.currentSlide > 0) {
      this.currentSlide--
      this.updateView()
    }
  }

  next() {
    const maxSlides = this.getMaxSlides()
    if (this.currentSlide < maxSlides) {
      this.currentSlide++
      this.updateView()
    }
  }

  goToSlide(event) {
  const slideIndex = parseInt(event.currentTarget.dataset.slideIndex, 10) || 0
  const maxSlides = this.getMaxSlides()
  this.currentSlide = Math.max(0, Math.min(slideIndex, maxSlides))
  this.updateView()
}

  updateView() {
    const itemsPerView = this.getCurrentItemsPerView()
    const slideWidth = 100 / itemsPerView
    const translateX = -(this.currentSlide * slideWidth)
    
    this.sliderTarget.style.transform = `translateX(${translateX}%)`
    
    // Update navigation buttons
    this.prevBtnTarget.disabled = this.currentSlide === 0
    this.nextBtnTarget.disabled = this.currentSlide >= this.getMaxSlides()
    
    // Update dots
    this.updateDots()
  }

  updateDots() {
    const maxSlides = this.getMaxSlides()
    const totalDots = maxSlides + 1
    
    // Hide dots if only one slide
    if (totalDots <= 1) {
      this.dotsTarget.style.display = 'none'
      return
    } else {
      this.dotsTarget.style.display = 'flex'
    }
    
    this.dotTargets.forEach((dot, index) => {
      if (index < totalDots) {
        dot.style.display = 'block'
        dot.classList.toggle('bg-ph24_blue', index === this.currentSlide)
        dot.classList.toggle('bg-gray-300', index !== this.currentSlide)
      } else {
        dot.style.display = 'none'
      }
    })
  }

  handleResize() {
    // Recalculate and update view on resize
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      const maxSlides = this.getMaxSlides()
      this.currentSlide = Math.min(this.currentSlide, maxSlides)
      this.updateView()
    }, 100)
  }

  setupTouchEvents() {
    let startX = 0
    let currentX = 0
    let isDragging = false
    const slider = this.sliderTarget

    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX
      isDragging = true
      slider.style.transition = 'none'
    }

    const handleTouchMove = (e) => {
      if (!isDragging) return
      e.preventDefault()
      
      currentX = e.touches[0].clientX
      const diffX = currentX - startX
      const itemsPerView = this.getCurrentItemsPerView()
      const slideWidth = 100 / itemsPerView
      const currentTranslate = -(this.currentSlide * slideWidth)
      const newTranslate = currentTranslate + (diffX / slider.offsetWidth * 100)
      
      slider.style.transform = `translateX(${newTranslate}%)`
    }

    const handleTouchEnd = (e) => {
      if (!isDragging) return
      isDragging = false
      slider.style.transition = 'transform 0.5s ease-in-out'
      
      const diffX = currentX - startX
      const threshold = 50 // minimum distance to trigger slide change
      
      if (Math.abs(diffX) > threshold) {
        if (diffX > 0 && this.currentSlide > 0) {
          this.prev()
        } else if (diffX < 0 && this.currentSlide < this.getMaxSlides()) {
          this.next()
        } else {
          this.updateView() // snap back
        }
      } else {
        this.updateView() // snap back
      }
    }

    // Store bound handlers as instance properties
    this.boundHandleTouchStart = handleTouchStart
    this.boundHandleTouchMove = handleTouchMove
    this.boundHandleTouchEnd = handleTouchEnd

    slider.addEventListener('touchstart', this.boundHandleTouchStart, { passive: false })
    slider.addEventListener('touchmove', this.boundHandleTouchMove, { passive: false })
    slider.addEventListener('touchend', this.boundHandleTouchEnd, { passive: true })
  }

  removeTouchEvents() {
    const slider = this.sliderTarget
    
    // Remove touch event listeners using the stored bound references
    if (this.boundHandleTouchStart) {
      slider.removeEventListener('touchstart', this.boundHandleTouchStart)
    }
    if (this.boundHandleTouchMove) {
      slider.removeEventListener('touchmove', this.boundHandleTouchMove)
    }
    if (this.boundHandleTouchEnd) {
      slider.removeEventListener('touchend', this.boundHandleTouchEnd)
    }
  }
}